/**
 * Resumable chunked uploads (web-plan.md F2, chat-api.md §2.6): sha256 over
 * the file BEFORE any byte moves (the dedup), then 1 MiB PATCHes whose
 * progress is the server's count and never ours, then `finish`.
 *
 * The three "the upload was destroyed" cases (sha mismatch, sniff refusal,
 * missing photo geometry) all answer 404 on a repeat `finish` — the caller
 * starts over from `POST /v1/uploads`; nothing here retries them.
 */
import type { FileKind } from "../../gen/dto";

import { session } from "../session";
import { assertCurrent, currentLifetime, type SessionLifetime } from "../lifetime";

export const CHUNK_SIZE = 1024 * 1024;
const BULK_TIMEOUT_MS = 60_000;
const CONTROL_TIMEOUT_MS = 10_000;

export interface UploadResult {
  file_id: string;
  width?: number;
  height?: number;
  mime?: string;
  size: number;
}

export class UploadError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const KIND_CEILINGS: Record<string, number> = {
  // The *original* budget (D233), not the preview budget: a compliant client
  // uploads at most a 300 KiB preview under this kind, but avatars take the
  // same door and so do clients older than D233.
  photo: 5 * 1024 * 1024,
  voice: 8 * 1024 * 1024,
  video: 100 * 1024 * 1024,
  video_message: 100 * 1024 * 1024,
  file: 100 * 1024 * 1024,
};

export function kindCeiling(kind: FileKind): number {
  return KIND_CEILINGS[kind] ?? 0;
}

export interface BeginExtras {
  width?: number;
  height?: number;
  durationMs?: number;
  fileName?: string;
}

/** The whole three-step protocol. `progress` is 0..=1 and must not block. */
export async function upload(
  blob: Blob,
  kind: FileKind,
  extras: BeginExtras,
  progress: (fraction: number) => void,
  opts: { unlimited?: boolean } = {},
): Promise<UploadResult> {
  const lifetime = currentLifetime();
  const account = session();
  if (!account) throw new UploadError(0, "not logged in");
  const context = { lifetime, jwt: account.jwt };
  if (blob.size === 0) throw new UploadError(0, "empty file");
  const ceiling = kindCeiling(kind);
  if (!opts.unlimited && blob.size > ceiling) {
    throw new UploadError(
      0,
      `${Math.floor(blob.size / (1024 * 1024))} MiB is over the ${Math.floor(
        ceiling / (1024 * 1024),
      )} MiB ceiling for ${kind}`,
    );
  }

  // sha256 first — what makes the dedup possible. crypto.subtle wants the
  // whole buffer; at a 100 MiB ceiling that is an accepted cost (the
  // desktop streams in 256 KiB reads because files there can be anything).
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  assertCurrent(lifetime);
  const sha256 = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");

  const beginBody: Record<string, unknown> = {
    size: blob.size,
    sha256,
    // The server sniffs the real type from the bytes at finish; the declared
    // value only has to be in the allow-list for the kind.
    mime: blob.type || "application/octet-stream",
    kind,
  };
  if (extras.fileName !== undefined) beginBody["file_name"] = extras.fileName;
  if (extras.width !== undefined) beginBody["width"] = extras.width;
  if (extras.height !== undefined) beginBody["height"] = extras.height;
  if (extras.durationMs !== undefined) beginBody["duration_ms"] = extras.durationMs;

  const begin = await json<{ file_id?: string; upload_id?: string; received?: number }>(
    "POST",
    "/v1/uploads",
    beginBody,
    CONTROL_TIMEOUT_MS,
    context,
  );
  assertCurrent(lifetime);

  // Deduped: the bytes are already there, nothing to transfer.
  if (begin.file_id !== undefined) {
    progress(1);
    return {
      file_id: begin.file_id,
      size: blob.size,
      ...(extras.width !== undefined ? { width: extras.width } : {}),
      ...(extras.height !== undefined ? { height: extras.height } : {}),
    };
  }

  const uploadId = begin.upload_id!;
  let received = begin.received ?? 0;

  while (received < blob.size) {
    const chunk = blob.slice(received, received + CHUNK_SIZE);
    const answer = await bytes<{ received: number }>(
      "PATCH",
      `/v1/uploads/${uploadId}?offset=${received}`,
      chunk,
      context,
    );
    assertCurrent(lifetime);
    // Trust the server's count, not ours: a resend after a retry must not
    // double-advance, and a non-advancing answer must not spin forever.
    if (answer.received <= received) {
      throw new UploadError(0, `upload stalled at ${received} bytes`);
    }
    received = answer.received;
    progress(received / blob.size);
  }

  const fin = await json<{ file_id: string; width?: number; height?: number; mime?: string; size?: number }>(
    "POST",
    `/v1/uploads/${uploadId}/finish`,
    {},
    CONTROL_TIMEOUT_MS,
    context,
  );
  assertCurrent(lifetime);
  progress(1);
  return {
    file_id: fin.file_id,
    size: fin.size ?? blob.size,
    ...(fin.width !== undefined
      ? { width: fin.width }
      : extras.width !== undefined
        ? { width: extras.width }
        : {}),
    ...(fin.height !== undefined
      ? { height: fin.height }
      : extras.height !== undefined
        ? { height: extras.height }
        : {}),
    ...(fin.mime !== undefined ? { mime: fin.mime } : {}),
  };
}

interface UploadContext {
  lifetime: SessionLifetime;
  jwt: string;
}

async function json<T>(
  method: string,
  path: string,
  body: unknown,
  timeoutMs: number,
  context: UploadContext,
): Promise<T> {
  assertCurrent(context.lifetime);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${context.jwt}`,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.any([context.lifetime.signal, AbortSignal.timeout(timeoutMs)]),
    });
  } catch (e) {
    throw new UploadError(0, e instanceof Error ? e.message : String(e));
  }
  assertCurrent(context.lifetime);
  const result = await answer<T>(response, path);
  assertCurrent(context.lifetime);
  return result;
}

async function bytes<T>(method: string, path: string, chunk: Blob, context: UploadContext): Promise<T> {
  assertCurrent(context.lifetime);
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: { Authorization: `Bearer ${context.jwt}` },
      body: chunk,
      signal: AbortSignal.any([context.lifetime.signal, AbortSignal.timeout(BULK_TIMEOUT_MS)]),
    });
  } catch (e) {
    throw new UploadError(0, e instanceof Error ? e.message : String(e));
  }
  assertCurrent(context.lifetime);
  const result = await answer<T>(response, path);
  assertCurrent(context.lifetime);
  return result;
}

async function answer<T>(response: Response, path: string): Promise<T> {
  const text = await response.text().catch(() => "");
  if (response.status < 200 || response.status >= 300) {
    let message = `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (typeof parsed.error === "string") message = parsed.error;
    } catch {
      // The status is the message.
    }
    throw new UploadError(response.status, message);
  }
  try {
    return JSON.parse(text) as T;
  } catch (e) {
    throw new UploadError(0, `${path}: malformed response: ${String(e)}`);
  }
}
